import { Helmet } from 'react-helmet-async';

// Social sharing image URL - UPA splash badge (square, optimised for WhatsApp/Facebook/Twitter previews)
const DEFAULT_OG_IMAGE = 'https://customer-assets.emergentagent.com/job_4cf29391-a054-46ca-8c58-6d2c15185384/artifacts/dhwdlt33_black%20back.PNG';
const SITE_NAME = 'Universal Performing Arts';
const BASE_URL = 'https://www.upaschool.com';

// Schema.org structured data for the organization
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": ["PerformingArtsTheater", "School"],
  "name": "Universal Performing Arts",
  "alternateName": ["UPA", "UPA School", "UPA Milton Keynes"],
  "url": BASE_URL,
  "logo": DEFAULT_OG_IMAGE,
  "image": DEFAULT_OG_IMAGE,
  "description": "Theatre school in Milton Keynes offering musical theatre, acting, singing and dance classes for children and teenagers aged 4–18.",
  "telephone": "+44-1234-567890",
  "email": "theteam@upaschool.com",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Kents Hill Secondary School",
    "addressLocality": "Milton Keynes",
    "addressRegion": "Buckinghamshire",
    "postalCode": "MK7 6BZ",
    "addressCountry": "GB"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "52.0066",
    "longitude": "-0.7136"
  },
  "areaServed": [
    {"@type": "City", "name": "Milton Keynes"},
    {"@type": "AdministrativeArea", "name": "Buckinghamshire"}
  ],
  "knowsAbout": [
    "musical theatre", "acting", "singing", "dance",
    "performing arts", "drama", "stagecraft", "choreography"
  ],
  "sameAs": [
    "https://www.facebook.com/upaschool",
    "https://www.instagram.com/upaschool"
  ],
  "priceRange": "££",
  "openingHours": "Sa 09:00-17:00",
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "5",
    "reviewCount": "50",
    "bestRating": "5"
  },
  "review": [
    {
      "@type": "Review",
      "reviewRating": {"@type": "Rating", "ratingValue": "5", "bestRating": "5"},
      "author": {"@type": "Person", "name": "UPA Parent"},
      "reviewBody": "UPA is the best theatre school in Milton Keynes. My daughter has grown in confidence and loves every class."
    },
    {
      "@type": "Review",
      "reviewRating": {"@type": "Rating", "ratingValue": "5", "bestRating": "5"},
      "author": {"@type": "Person", "name": "UPA Parent"},
      "reviewBody": "The teachers are inspiring, supportive and professional."
    },
    {
      "@type": "Review",
      "reviewRating": {"@type": "Rating", "ratingValue": "5", "bestRating": "5"},
      "author": {"@type": "Person", "name": "UPA Parent"},
      "reviewBody": "The productions are outstanding and every child gets a chance to shine."
    }
  ]
};

// FAQ schema — surfaced on home + faq pages
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What age groups do you teach?",
      "acceptedAnswer": {"@type": "Answer", "text": "We offer performing arts classes in Milton Keynes for children and teenagers aged 4 to 18, Primatotterinas for ages 3–5, plus adult classes including Adult Street Dance."}
    },
    {
      "@type": "Question",
      "name": "Do students need experience?",
      "acceptedAnswer": {"@type": "Answer", "text": "No. Beginners are always welcome. We tailor classes for every level."}
    },
    {
      "@type": "Question",
      "name": "What standard of training will my child get?",
      "acceptedAnswer": {"@type": "Answer", "text": "The very best. UPA only brings in industry professionals — West End performers, working actors, TV credits, professional choreographers and vocal coaches — so your child trains the way real performers train."}
    },
    {
      "@type": "Question",
      "name": "Where are classes held?",
      "acceptedAnswer": {"@type": "Answer", "text": "Classes take place at Kents Hill Secondary School in Milton Keynes (MK7 6BZ)."}
    },
    {
      "@type": "Question",
      "name": "Can my child try a class?",
      "acceptedAnswer": {"@type": "Answer", "text": "Yes — we offer free trial sessions subject to availability. Book online via our Free Trial page."}
    },
    {
      "@type": "Question",
      "name": "Is there a sibling discount?",
      "acceptedAnswer": {"@type": "Answer", "text": "Yes. We offer 30% off for every additional sibling, applied automatically when you book online."}
    }
  ]
};

// Schema for educational courses
const courseSchema = {
  "@context": "https://schema.org",
  "@type": "Course",
  "provider": {
    "@type": "Organization",
    "name": "Universal Performing Arts",
    "sameAs": BASE_URL
  },
  "name": "Performing Arts Training",
  "description": "Professional training in Dance, Drama, and Singing for young performers aged 4-18",
  "educationalCredentialAwarded": "Certificate of Completion",
  "hasCourseInstance": {
    "@type": "CourseInstance",
    "courseMode": "onsite",
    "instructor": {
      "@type": "Person",
      "name": "UPA Faculty"
    }
  }
};

// Page configurations for SEO
export const pageConfigs = {
  home: {
    title: 'Theatre School in Milton Keynes | Acting, Singing & Dance Classes | UPA',
    description: 'UPA offers musical theatre, acting, singing and dance classes for children and teenagers in Milton Keynes. Build confidence and perform in exciting productions. Book a free trial.',
    keywords: 'theatre school milton keynes, performing arts school milton keynes, musical theatre classes milton keynes, acting classes for children milton keynes, singing classes milton keynes, dance classes milton keynes, drama school milton keynes, kids theatre classes milton keynes',
    canonical: '/'
  },
  classes: {
    title: 'Musical Theatre, Acting, Singing & Dance Classes Milton Keynes | UPA',
    description: 'Weekly performing arts classes in Milton Keynes for ages 4–18. Combined musical theatre, acting, singing and dance training. View timetable and book a free trial.',
    keywords: 'musical theatre classes milton keynes, dance classes milton keynes, singing classes milton keynes, acting classes milton keynes',
    canonical: '/classes'
  },
  about: {
    title: 'About Universal Performing Arts | Theatre School Milton Keynes',
    description: 'Universal Performing Arts (UPA) is a specialist theatre school in Milton Keynes for ages 4–18. Acting, singing and dance training in a supportive environment.',
    keywords: 'about upa, theatre school milton keynes, performing arts school milton keynes',
    canonical: '/about'
  },
  contact: {
    title: 'Contact UPA | Theatre School in Milton Keynes',
    description: 'Contact Universal Performing Arts to book a trial class and discover how acting, singing and dance can help your child grow in confidence. Located in Milton Keynes.',
    keywords: 'contact upa, theatre school milton keynes contact, performing arts enquiries milton keynes',
    canonical: '/contact'
  },
  freeTrial: {
    title: 'Free Trial Class | UPA Theatre School Milton Keynes',
    description: 'Book a FREE trial class at UPA in Milton Keynes. Experience musical theatre, acting, singing and dance training with no obligation.',
    keywords: 'free trial performing arts milton keynes, free drama class milton keynes, free dance lesson milton keynes',
    canonical: '/free-trial'
  },
  enroll: {
    title: 'Book a Free Trial | UPA Theatre School Milton Keynes',
    description: 'Sign up for a free trial at Universal Performing Arts. Try our musical theatre, acting, singing and dance classes in Milton Keynes.',
    keywords: 'free trial performing arts milton keynes, try drama school milton keynes, sign up dance classes milton keynes',
    canonical: '/free-trial'
  },
  schedule: {
    title: 'Class Timetable | UPA Theatre School Milton Keynes',
    description: 'See the weekly timetable for musical theatre, acting, singing and dance classes at UPA in Milton Keynes. Find a time slot that suits your family.',
    keywords: 'class timetable milton keynes, performing arts schedule milton keynes',
    canonical: '/schedule'
  },
  camps: {
    title: 'Holiday Camps & West End Bootcamps Milton Keynes | UPA',
    description: 'School-holiday performing arts camps and West End bootcamps in Milton Keynes. Acting, singing and dance training led by industry professionals.',
    keywords: 'holiday camps milton keynes, drama bootcamp milton keynes, dance workshop milton keynes, west end bootcamp',
    canonical: '/bootcamps'
  },
  prospectus: {
    title: 'UPA School Prospectus | Theatre School Milton Keynes',
    description: 'Download the UPA prospectus to learn about our musical theatre, acting, singing and dance classes for ages 4–18 in Milton Keynes.',
    keywords: 'upa prospectus, theatre school prospectus milton keynes',
    canonical: '/prospectus'
  },
  team: {
    title: 'Meet the Team | UPA Theatre School Milton Keynes',
    description: 'Meet UPA\'s teachers and staff — West End performers and experienced educators training young performers across Milton Keynes.',
    keywords: 'upa teachers, performing arts faculty milton keynes, drama instructors milton keynes',
    canonical: '/team'
  },
  uniform: {
    title: 'Uniform & Dress Code | UPA Milton Keynes',
    description: 'What to wear for UPA musical theatre, acting, singing and dance classes in Milton Keynes. Uniform information for students and parents.',
    keywords: 'upa uniform, theatre school dress code milton keynes',
    canonical: '/uniform'
  },
  gallery: {
    title: 'Gallery | UPA Theatre School Milton Keynes',
    description: 'Photos and videos from UPA performances, classes and events in Milton Keynes. See our young performers in action.',
    keywords: 'upa gallery, theatre school photos milton keynes, performance gallery',
    canonical: '/gallery'
  },
  workshops: {
    title: 'Workshops | UPA Theatre School Milton Keynes',
    description: 'Specialist musical theatre, acting, singing and dance workshops in Milton Keynes — intensive sessions with industry professionals.',
    keywords: 'performing arts workshops milton keynes, drama masterclass milton keynes, singing workshop milton keynes',
    canonical: '/workshops'
  },
  showChoir: {
    title: 'Show Choir | UPA Theatre School Milton Keynes',
    description: 'Join the UPA Show Choir in Milton Keynes — combined singing, dancing and performance perfect for young musical theatre fans.',
    keywords: 'show choir milton keynes, musical theatre choir milton keynes, performance choir',
    canonical: '/show-choir'
  },
  primatotterinas: {
    title: 'Primatotterinas — Pre-school Performing Arts Milton Keynes | UPA',
    description: 'Primatotterinas is UPA\'s early-years performing arts class in Milton Keynes for ages 3–5. Introduction to dance, singing and creative play.',
    keywords: 'primatotterinas, toddler dance classes milton keynes, preschool performing arts milton keynes',
    canonical: '/primatotterinas'
  },
  // SEO landing pages — exact-match keyword URLs
  musicalTheatre: {
    title: 'Musical Theatre Classes in Milton Keynes | UPA',
    description: 'UPA\'s musical theatre classes in Milton Keynes combine acting, singing and dance for ages 4–18. Build confidence and perform in full-scale productions.',
    keywords: 'musical theatre classes milton keynes, musical theatre school milton keynes',
    canonical: '/musical-theatre-classes-milton-keynes'
  },
  acting: {
    title: 'Acting Classes for Children in Milton Keynes | UPA',
    description: 'Engaging acting classes for children in Milton Keynes. Characterisation, script work, improvisation and stage presence in a supportive environment.',
    keywords: 'acting classes for children milton keynes, drama classes milton keynes, kids acting milton keynes',
    canonical: '/acting-classes-milton-keynes'
  },
  singing: {
    title: 'Singing Classes in Milton Keynes | UPA',
    description: 'Singing classes in Milton Keynes for children and teenagers. Vocal technique, pitch, harmony and performance confidence — ages 4–18.',
    keywords: 'singing classes milton keynes, vocal lessons milton keynes, kids singing milton keynes',
    canonical: '/singing-classes-milton-keynes'
  },
  dance: {
    title: 'Dance Classes in Milton Keynes | UPA Performing Arts',
    description: 'Dance classes in Milton Keynes for ages 4–18. Musical theatre choreography, movement and performance — for beginners through advanced.',
    keywords: 'dance classes milton keynes, kids dance milton keynes, musical theatre dance milton keynes',
    canonical: '/dance-classes-milton-keynes'
  },
  theatreSchool: {
    title: 'Theatre School in Milton Keynes | Universal Performing Arts',
    description: 'UPA is a leading theatre school in Milton Keynes for ages 4–18. Acting, singing and dance training in one weekly class. Book a free trial today.',
    keywords: 'theatre school milton keynes, drama school milton keynes, performing arts school milton keynes',
    canonical: '/theatre-school-milton-keynes'
  },
  login: {
    title: 'Login | UPA School',
    description: 'Login to your Universal Performing Arts parent portal. Manage bookings, view schedules, and stay connected with your child\'s performing arts journey.',
    keywords: 'parent login, upa portal, student account',
    canonical: '/login'
  },
  dashboard: {
    title: 'Parent Dashboard | UPA School',
    description: 'Your Universal Performing Arts dashboard. View your children\'s classes, manage payments, and track their performing arts progress.',
    keywords: 'parent dashboard, manage bookings, upa account',
    canonical: '/dashboard'
  },
  admin: {
    title: 'Admin Dashboard | UPA School',
    description: 'Universal Performing Arts administration portal.',
    keywords: '',
    canonical: '/admin'
  }
};

const SEO = ({ 
  page = 'home', 
  title, 
  description, 
  image,
  url,
  type = 'website',
  includeSchema = true,
  noIndex = false
}) => {
  const config = pageConfigs[page] || pageConfigs.home;
  
  const finalTitle = title || config.title;
  const finalDescription = description || config.description;
  const finalImage = image || DEFAULT_OG_IMAGE;
  const canonicalPath = config.canonical || '/';
  const finalUrl = url || `${BASE_URL}${canonicalPath}`;
  
  // Admin/auth pages should not be indexed
  const isAdminPage = ['admin', 'dashboard', 'login'].includes(page);
  const shouldNoIndex = noIndex || isAdminPage;

  // Generate page-specific schema
  const getPageSchema = () => {
    switch (page) {
      case 'home':
        return [organizationSchema];
      case 'classes':
        return [organizationSchema, courseSchema];
      case 'freeTrial':
        return [organizationSchema, {
          "@context": "https://schema.org",
          "@type": "Event",
          "name": "Free Trial Class at Universal Performing Arts",
          "description": "Experience a FREE trial class in Dance, Drama or Singing",
          "eventStatus": "https://schema.org/EventScheduled",
          "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
          "location": {
            "@type": "Place",
            "name": "Universal Performing Arts",
            "address": organizationSchema.address
          },
          "organizer": {
            "@type": "Organization",
            "name": "Universal Performing Arts",
            "url": BASE_URL
          },
          "isAccessibleForFree": true,
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "GBP",
            "availability": "https://schema.org/InStock"
          }
        }];
      case 'team':
        return [organizationSchema, {
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "Meet the Team",
          "description": config.description,
          "mainEntity": {
            "@type": "Organization",
            "name": "Universal Performing Arts"
          }
        }];
      default:
        return includeSchema ? [organizationSchema] : [];
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {config.keywords && <meta name="keywords" content={config.keywords} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={finalUrl} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
      
      {/* Additional SEO Tags */}
      <meta name="robots" content={shouldNoIndex ? "noindex, nofollow" : "index, follow"} />
      <meta name="author" content="Universal Performing Arts" />
      {!shouldNoIndex && <link rel="canonical" href={finalUrl} />}
      
      {/* Schema.org Structured Data */}
      {includeSchema && getPageSchema().map((schema, index) => (
        <script 
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </Helmet>
  );
};

export default SEO;
